import { ec2ApiRequest } from "../authConfig";

// Spring Boot backend endpoint that proxies AWS EC2 Managed Prefix List operations.
// AWS credentials live server-side and are never exposed to the browser.
// Authentication uses Azure AD Bearer tokens (MSAL) — no static password in the bundle.
const EC2_API_ENDPOINT =
  process.env.REACT_APP_EC2_API_ENDPOINT || "http://localhost:9091";

/**
 * Typed error so callers can distinguish "no token" (MSAL silent failure)
 * from "backend rejected request" (HTTP non-OK) and "network unreachable".
 */
class Ec2ApiError extends Error {
  constructor(kind, message, cause) {
    super(message);
    this.name = "Ec2ApiError";
    this.kind = kind; // "auth" | "not_found" | "http_error" | "network"
    this.cause = cause;
  }
}

/**
 * Acquires an Azure AD access token for the EC2 API backend via MSAL.
 * On silent failure attempts ssoSilent, then acquireTokenRedirect as a last resort.
 * @param instance MSAL PublicClientApplication instance
 * @param account   The signed-in user's account object
 * @returns {Promise<string>} the access token
 * @throws {Ec2ApiError} with kind "auth" if no token can be obtained
 */
async function getAccessToken(instance, account) {
  if (!instance || !account) {
    throw new Ec2ApiError("auth", "MSAL instance or account missing");
  }
  try {
    const response = await instance.acquireTokenSilent({
      ...ec2ApiRequest,
      account,
    });
    return response.accessToken;
  } catch (silentError) {
    console.error("MSAL acquireTokenSilent error:", silentError);
    try {
      const response = await instance.ssoSilent({
        ...ec2ApiRequest,
        account,
      });
      return response.accessToken;
    } catch (ssoError) {
      console.error("MSAL ssoSilent error:", ssoError);
      // Last resort: interactive redirect. Resolves after redirect completes;
      // if the redirect itself fails, surface a typed auth error to the caller.
      try {
        const response = await instance.acquireTokenRedirect({
          ...ec2ApiRequest,
          account,
        });
        return response.accessToken;
      } catch (redirectError) {
        console.error("MSAL acquireTokenRedirect error:", redirectError);
        throw new Ec2ApiError(
          "auth",
          "Could not acquire an Azure AD token. Please sign in again.",
          redirectError
        );
      }
    }
  }
}

/**
 * Fetches the managed prefix list entries for the given username prefix.
 * @param instance MSAL instance
 * @param account   Signed-in user account
 * @param {string} username
 * @returns {Promise<{prefixListId: string, currentVersion: string, prefixListEntries: Array<{cidr: string, description: string}>}>}
 * @throws {Ec2ApiError} kind "auth" | "not_found" | "http_error" | "network"
 */
async function fetchManagedPrefixListEntries(instance, account, username) {
  if (!username) {
    throw new Ec2ApiError("auth", "No username provided");
  }

  const token = await getAccessToken(instance, account);

  let response;
  try {
    response = await fetch(
      `${EC2_API_ENDPOINT}/ec2/prefix-list-entries?username=${encodeURIComponent(username)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error) {
    console.error("Fetch error:", error);
    throw new Ec2ApiError(
      "network",
      "Could not reach the EC2 API service. It may be down or unreachable.",
      error
    );
  }

  if (response.status === 404) {
    throw new Ec2ApiError(
      "not_found",
      "No prefix list is registered for your username."
    );
  }
  if (response.status === 401 || response.status === 403) {
    throw new Ec2ApiError(
      "auth",
      "Your session is no longer valid. Please sign in again."
    );
  }
  if (!response.ok) {
    throw new Ec2ApiError(
      "http_error",
      `The EC2 API service returned an unexpected status (${response.status}).`
    );
  }

  const data = await response.json();

  // Map backend `entries` to the shape App.jsx expects: `prefixListEntries`.
  return {
    prefixListId: data.prefixListId,
    currentVersion: data.currentVersion,
    prefixListEntries: data.entries,
  };
}

/**
 * Modifies a managed prefix list by removing and/or adding an entry.
 * @returns {Promise<boolean>} true on success
 * @throws {Ec2ApiError} kind "auth" | "http_error" | "network"
 */
async function modifyManagedPrefixList(
  instance,
  account,
  prefixListId,
  currentVersion,
  removeEntryCidr,
  addEntryCidr,
  addEntryDescription
) {
  const token = await getAccessToken(instance, account);

  const params = new URLSearchParams({
    prefixListId,
    currentVersion,
  });
  if (removeEntryCidr) params.append("removeEntryCidr", removeEntryCidr);
  if (addEntryCidr) params.append("addEntryCidr", addEntryCidr);
  if (addEntryDescription) params.append("addEntryDescription", addEntryDescription);

  let response;
  try {
    response = await fetch(
      `${EC2_API_ENDPOINT}/ec2/modify-prefix-list?${params.toString()}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error) {
    console.error("Fetch error:", error);
    throw new Ec2ApiError(
      "network",
      "Could not reach the EC2 API service. It may be down or unreachable.",
      error
    );
  }

  if (response.status === 401 || response.status === 403) {
    throw new Ec2ApiError(
      "auth",
      "Your session is no longer valid. Please sign in again."
    );
  }
  if (!response.ok) {
    throw new Ec2ApiError(
      "http_error",
      `The EC2 API service returned an unexpected status (${response.status}).`
    );
  }
  return true;
}

/**
 * Returns the caller's public IP address with a /32 suffix.
 * Fetches directly from ipify in the browser so we get the user's true
 * public IP, not the IP of an intermediate proxy/backend server.
 * @returns {Promise<string | null>}
 */
async function getMyPublicIp() {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) return null;
    const data = await response.json();
    if (!data.ip) return null;
    return data.ip + "/32";
  } catch (error) {
    console.error("Fetch error:", error);
    return null;
  }
}

export { Ec2ApiError, fetchManagedPrefixListEntries, getMyPublicIp, modifyManagedPrefixList };